import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, X, Download, ArrowLeft, FileDown, AlertTriangle,
  CheckCircle2, Clock, XCircle, Eye, Printer,
} from "lucide-react";

const money = (n) =>
  `RD$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CAT_META = {
  vigente:        { label: "Vigente",         color: "text-emerald-700 dark:text-emerald-400", bg: "bg-emerald-500/10", icon: CheckCircle2 },
  vencida_gracia: { label: "En gracia",       color: "text-amber-700 dark:text-amber-400",     bg: "bg-amber-500/10",   icon: Clock },
  fuera_plazo:    { label: "Fuera de plazo",  color: "text-red-700 dark:text-red-400",         bg: "bg-red-500/10",     icon: XCircle },
};

// ─────────────────────────────── SUMMARY PAGE ───────────────────────────────
export default function ReceivablesAging() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");   // all | vigente | vencida_gracia | fuera_plazo
  const [includeMigration, setIncludeMigration] = useState("true"); // true | false | only (Iter 45)
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const f = searchParams.get("filter");
    if (f && CAT_META[f]) setFilter(f);
  }, [searchParams]);

  const load = async () => {
    try {
      const { data } = await api.get(`/accounting/receivables/by-customer?include_migration=${includeMigration}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, [includeMigration]);

  const items = useMemo(() => {
    if (!data) return [];
    let list = data.items;
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((it) =>
        (it.customer_name || "").toLowerCase().includes(s) ||
        (it.rnc_cedula || "").toLowerCase().includes(s) ||
        (it.phone || "").toLowerCase().includes(s)
      );
    }
    if (filter !== "all") {
      list = list.filter((it) => (it[filter]?.amount || 0) > 0);
    }
    return list;
  }, [data, q, filter]);

  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/accounting/receivables/by-customer.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `cxc_por_cliente_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <PageContainer><div className="p-8 text-center text-muted-foreground">Cargando…</div></PageContainer>;
  const t = data.totals;

  return (
    <PageContainer>
      <PageHeader
        title="Antigüedad de Cuentas por Cobrar"
        subtitle="Saldos pendientes por cliente clasificados según la política de crédito de cada uno."
        testid="receivables-aging-header"
      />

      {/* KPI cards clickables (funcionan como filtro) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiButton
          active={filter === "all"} onClick={() => setFilter("all")}
          label="Total pendiente" value={money(t.total_pending)}
          hint={`${t.customers_count} cliente${t.customers_count === 1 ? "" : "s"}`}
          testid="kpi-total" primary />
        <KpiButton
          active={filter === "vigente"} onClick={() => setFilter(filter === "vigente" ? "all" : "vigente")}
          label="Vigente" value={money(t.vigente)}
          hint="Dentro del plazo de crédito"
          testid="kpi-vigente"
          className="bg-emerald-500/10 border-emerald-500/40" labelClass="text-emerald-700 dark:text-emerald-400" />
        <KpiButton
          active={filter === "vencida_gracia"} onClick={() => setFilter(filter === "vencida_gracia" ? "all" : "vencida_gracia")}
          label="Vencida — En gracia" value={money(t.vencida_gracia)}
          hint="Pasado plazo pero antes del tope"
          testid="kpi-gracia"
          className="bg-amber-500/10 border-amber-500/40" labelClass="text-amber-700 dark:text-amber-400" />
        <KpiButton
          active={filter === "fuera_plazo"} onClick={() => setFilter(filter === "fuera_plazo" ? "all" : "fuera_plazo")}
          label="Fuera de plazo" value={money(t.fuera_plazo)}
          hint={`${t.customers_blocked} cliente${t.customers_blocked === 1 ? "" : "s"} bloqueado${t.customers_blocked === 1 ? "" : "s"}`}
          testid="kpi-fuera"
          className="bg-red-500/10 border-red-500/40" labelClass="text-red-700 dark:text-red-400" />
      </div>

      {/* Filtro + acciones */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por cliente, RNC o teléfono…"
            className="h-8 max-w-md" data-testid="receivables-search"
          />
          {q && <Button size="sm" variant="ghost" onClick={() => setQ("")}><X className="w-4 h-4" /></Button>}
        </div>
        {filter !== "all" && (
          <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary font-medium">
            Filtro: {CAT_META[filter].label}
            <button onClick={() => setFilter("all")} className="ml-1" data-testid="clear-filter"><X className="w-3 h-3 inline" /></button>
          </span>
        )}
        <span className="text-xs text-muted-foreground">{items.length} cliente{items.length === 1 ? "" : "s"}</span>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-summary-pdf">
          <Printer className="w-4 h-4 mr-1.5" /> Imprimir resumen
        </Button>
      </div>

      {/* Toggle de origen (Iter 45) */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 bg-muted/30 border border-border rounded-md p-2.5 text-xs">
        <div className="flex items-center gap-3">
          <span className="overline text-[10px]">Origen de saldos:</span>
          {[
            { v: "true", label: "Todo (facturas + migrado)" },
            { v: "false", label: "Sólo facturas" },
            { v: "only", label: "Sólo CxC migrada" },
          ].map((o) => (
            <label key={o.v} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="rec-migsrc"
                value={o.v}
                checked={includeMigration === o.v}
                onChange={() => setIncludeMigration(o.v)}
                data-testid={`rec-migsrc-${o.v}`}
                className="accent-primary"
              />
              {o.label}
            </label>
          ))}
        </div>
        {data?.totals?.by_source && (
          <div className="text-[11px] data-cell text-muted-foreground">
            Facturas: <b className="text-foreground">{money(data.totals.by_source.invoice)}</b>
            {Number(data.totals.by_source.migration || 0) > 0 && (
              <span className="ml-3">Migración: <b className="text-amber-700 dark:text-amber-400">{money(data.totals.by_source.migration)}</b></span>
            )}
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Cliente</th>
              <th className="text-center p-3">Plazo cliente (días)</th>
              <th className="text-right p-3">
                <span className="text-emerald-700 dark:text-emerald-400">Vigente</span>
              </th>
              <th className="text-right p-3">
                <span className="text-amber-700 dark:text-amber-400">Vencida en gracia</span>
              </th>
              <th className="text-right p-3">
                <span className="text-red-700 dark:text-red-400">Fuera de plazo</span>
              </th>
              <th className="text-right p-3">Total pendiente</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.customer_id}
                  className={`border-b border-border/60 hover:bg-accent ${it.is_blocked ? "bg-red-500/5" : ""}`}
                  data-testid={`row-${it.customer_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="font-semibold">{it.customer_name}</div>
                      {it.rnc_cedula && <div className="text-[10px] data-cell text-muted-foreground">RNC {it.rnc_cedula}</div>}
                    </div>
                    {it.is_blocked && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/15 text-destructive font-bold uppercase whitespace-nowrap">
                        Bloqueado
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-center data-cell">
                  <div className="text-xs">Cred: <b>{it.credit_days}</b></div>
                  <div className="text-xs">Máx: <b>{it.max_overdue_days}</b></div>
                </td>
                <BucketCell b={it.vigente} kind="vigente" />
                <BucketCell b={it.vencida_gracia} kind="vencida_gracia" />
                <BucketCell b={it.fuera_plazo} kind="fuera_plazo" />
                <td className="p-3 text-right data-cell font-bold text-primary">{money(it.total_pending)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => navigate(`/receivables-aging/${it.customer_id}`)}
                    data-testid={`view-detail-${it.customer_id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" /> Detalle
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                Sin cuentas por cobrar {filter !== "all" ? `en categoría "${CAT_META[filter].label}"` : "pendientes"}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}

function KpiButton({ active, onClick, label, value, hint, testid, className = "", labelClass = "", primary }) {
  return (
    <button
      onClick={onClick}
      className={`text-left border rounded-md p-3 transition-all ${className || "border-border bg-card"} ${active ? "ring-2 ring-primary" : ""}`}
      data-testid={testid}
    >
      <div className={`overline ${labelClass || ""}`}>{label}</div>
      <div className={`text-2xl font-black mt-1 ${primary ? "text-primary" : ""}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </button>
  );
}

function BucketCell({ b, kind }) {
  if (!b || b.count === 0) {
    return <td className="p-3 text-right text-muted-foreground">—</td>;
  }
  const meta = CAT_META[kind];
  return (
    <td className="p-3 text-right">
      <div className={`data-cell font-bold ${meta.color}`}>{money(b.amount)}</div>
      <div className="text-[10px] text-muted-foreground data-cell">
        {b.count} fact · {b.max_days}d
      </div>
    </td>
  );
}

// ─────────────────────────────── DETAIL PAGE ───────────────────────────────
export function ReceivablesAgingDetail() {
  const { customerId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/accounting/receivables/by-customer/${customerId}`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err)));
  }, [customerId]);

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/accounting/receivables/by-customer/${customerId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `cxc_detalle_${customerId.slice(0,8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <PageContainer><div className="p-8 text-center text-muted-foreground">Cargando…</div></PageContainer>;
  const c = data.customer;
  const t = data.totals;

  return (
    <PageContainer>
      <PageHeader
        title={`CxC — ${c.name}`}
        subtitle="Detalle de facturas pendientes con clasificación por política de crédito."
        testid="receivables-detail-header"
      />

      <div className="flex items-center gap-2 mb-4">
        <Link to="/receivables-aging">
          <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1.5" /> Volver</Button>
        </Link>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-detail-pdf">
          <Printer className="w-4 h-4 mr-1.5" /> Imprimir detalle
        </Button>
      </div>

      {/* Cliente info */}
      <div className="bg-card border border-border rounded-md p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <InfoItem label="RNC / Cédula" value={c.rnc_cedula || "—"} mono />
          <InfoItem label="Teléfono" value={c.phone || "—"} mono />
          <InfoItem label="Días de crédito" value={c.credit_days} mono />
          <InfoItem label="Máx sin pagar (días)" value={c.max_overdue_days} mono />
          {(c.credit_limit || 0) > 0 && (
            <InfoItem label="Límite de crédito" value={money(c.credit_limit)} mono full />
          )}
        </div>
      </div>

      {/* Totales cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <TotalCard label="Vigente" value={money(t.vigente)} kind="vigente" />
        <TotalCard label="Vencida — En gracia" value={money(t.vencida_gracia)} kind="vencida_gracia" />
        <TotalCard label="Fuera de plazo" value={money(t.fuera_plazo)} kind="fuera_plazo" />
        <div className="border rounded-md p-3 border-primary/40 bg-primary/5">
          <div className="overline">Total pendiente</div>
          <div className="text-2xl font-black text-primary mt-1">{money(t.total_pending)}</div>
          <div className="text-xs text-muted-foreground mt-1">{t.invoices_count} factura{t.invoices_count === 1 ? "" : "s"}</div>
        </div>
      </div>

      {/* Facturas */}
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Factura</th>
              <th className="text-left p-3">NCF</th>
              <th className="text-left p-3">Emitida</th>
              <th className="text-left p-3">Vence</th>
              <th className="text-left p-3">Categoría / Días</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Pagado</th>
              <th className="text-right p-3">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((inv) => {
              const meta = CAT_META[inv.category];
              const Icon = meta.icon;
              return (
                <tr key={inv.invoice_id} className="border-b border-border/60 hover:bg-accent" data-testid={`invoice-${inv.invoice_id}`}>
                  <td className="p-3 font-bold data-cell">{inv.number}</td>
                  <td className="p-3 data-cell">{inv.ncf || "—"}</td>
                  <td className="p-3 data-cell">{inv.created_at}</td>
                  <td className="p-3 data-cell">{inv.due_date || "—"}</td>
                  <td className="p-3">
                    <div className={`flex items-center gap-1.5 text-xs font-bold ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                      {meta.label}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {inv.category === "vigente"
                        ? `Vence en ${inv.days_until_due} d`
                        : `Vencida por ${inv.days_overdue} d`}
                    </div>
                  </td>
                  <td className="p-3 text-right data-cell">{money(inv.total)}</td>
                  <td className="p-3 text-right data-cell text-muted-foreground">{money(inv.paid_amount)}</td>
                  <td className="p-3 text-right data-cell font-bold text-primary">{money(inv.balance_due)}</td>
                </tr>
              );
            })}
            {data.invoices.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Sin facturas pendientes.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}

function InfoItem({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2 md:col-span-4" : ""}>
      <div className="overline text-xs">{label}</div>
      <div className={`mt-0.5 ${mono ? "data-cell" : ""}`}>{value}</div>
    </div>
  );
}

function TotalCard({ label, value, kind }) {
  const meta = CAT_META[kind];
  const Icon = meta.icon;
  return (
    <div className={`border rounded-md p-3 ${meta.bg} border-transparent`}>
      <div className={`overline flex items-center gap-1.5 ${meta.color}`}>
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`text-2xl font-black mt-1 data-cell ${meta.color}`}>{value}</div>
    </div>
  );
}
